
import { useState, useEffect, useCallback } from 'react';
import { DebtEvent, DebtRecord, Property, Portfolio, FinancialInputs, HistoricalActualYear, ModelMetrics, PropertyAuditRecord, PropertyModelEvent, PropertyRiskProfile, PropertyDebtLink, SavedModelScenario } from '../types';
import { createDefaultRiskProfile, migrateLegacyRiskProfile, normalizeRiskProfile } from '../services/risk';
import { normalizeDebtRecord, withCalculatedDebtPayment } from '../services/debt';
import { migrateLegacyScenarioItems } from '../services/scenarios';

const STORAGE_KEY = 'mhp_properties_v2';
const PORTFOLIO_KEY = 'mhp_portfolios_v2';
const ORDER_KEY = 'mhp_property_order_v2';
const DEBT_KEY = 'mhp_debts_v1';
const DEBT_LINK_KEY = 'mhp_debt_links_v1';
const DEBT_EVENT_KEY = 'mhp_debt_events_v1';
const DEFAULT_BASE_YEAR = new Date().getFullYear();
const isDev = typeof window !== 'undefined' && ['localhost', '127.0.0.1'].includes(window.location.hostname);
const shouldLogPortfolioDnD = typeof window !== 'undefined' && isDev && window.localStorage.getItem('debug_portfolio_dnd') === '1';
const compareEventOrder = (left: DebtEvent, right: DebtEvent) => (
  left.date === right.date ? left.createdAt - right.createdAt : left.date.localeCompare(right.date)
);
const stripDerivedScenarioFields = (savedScenarios: SavedModelScenario[]) => (
  savedScenarios.map((scenario) => ({
    ...scenario,
    summary: null,
    lastRunAt: null
  }))
);
const buildPropertiesCachePayload = (properties: Record<string, Property>) => (
  Object.fromEntries(
    Object.entries(properties).map(([id, property]) => [id, {
      ...property,
      metrics: undefined,
      savedScenarios: stripDerivedScenarioFields(property.savedScenarios || [])
    }])
  )
);
export const usePropertyStore = () => {
  const [properties, setProperties] = useState({} as Record<string, Property>);
  const [portfolios, setPortfolios] = useState({} as Record<string, Portfolio>);
  const [propertyOrder, setPropertyOrder] = useState([] as string[]);
  const [debts, setDebts] = useState({} as Record<string, DebtRecord>);
  const [debtLinks, setDebtLinks] = useState([] as PropertyDebtLink[]);
  const [debtEvents, setDebtEvents] = useState([] as DebtEvent[]);
  const [isLoaded, setIsLoaded] = useState(false);

  const buildCanonicalDebtMap = useCallback((entries: DebtRecord[]) => {
    const next = {} as Record<string, DebtRecord>;
    entries.forEach((debt) => {
      const canonicalId = debt.supabaseId || debt.id;
      const normalizedDebt = debt.id === canonicalId
        ? debt
        : {
            ...debt,
            id: canonicalId
          };
      const existing = next[canonicalId];
      const nextTimestamp = normalizedDebt.updatedAt || normalizedDebt.createdAt || 0;
      const existingTimestamp = existing ? (existing.updatedAt || existing.createdAt || 0) : -1;

      // Prefer the newest copy when the same debt shows up once under a local id
      // and again under its Supabase id during hydration or sync.
      if (!existing || nextTimestamp >= existingTimestamp) {
        next[canonicalId] = normalizedDebt;
      }
    });
    return next;
  }, []);

  // Load from LocalStorage
  useEffect(() => {
    const savedProps = localStorage.getItem(STORAGE_KEY);
    const savedOrder = localStorage.getItem(ORDER_KEY);
    const savedPortfolios = localStorage.getItem(PORTFOLIO_KEY);
    const savedDebts = localStorage.getItem(DEBT_KEY);
    const savedDebtLinks = localStorage.getItem(DEBT_LINK_KEY);
    const savedDebtEvents = localStorage.getItem(DEBT_EVENT_KEY);
    const propertyIdMap = new Map<string, string>();
    
    if (savedProps) {
      const parsed = JSON.parse(savedProps);
      const migratedEntries = Object.entries(parsed).map(([id, property]: [string, any]) => {
          const canonicalId = property.supabaseId || property.id || id;
          propertyIdMap.set(id, canonicalId);
          const sourceInputs = property.currentYearInputs || property.inputs || {};
          const hasExplicitReserveInputs = Number(sourceInputs.reserveInputsVersion || 0) >= 1;
          const hasExplicitInvestmentInputs = Number(sourceInputs.investmentInputsVersion || 0) >= 1;
          const baseInputs: FinancialInputs = {
            purchasePrice: sourceInputs.purchasePrice ?? 0,
            downPayment: sourceInputs.downPayment ?? 0,
            acquisitionCosts: hasExplicitInvestmentInputs ? (sourceInputs.acquisitionCosts ?? 0) : 0,
            lenderFees: hasExplicitInvestmentInputs ? (sourceInputs.lenderFees ?? 0) : 0,
            initialCapex: hasExplicitInvestmentInputs ? (sourceInputs.initialCapex ?? 0) : 0,
            interestRate: sourceInputs.interestRate ?? 0,
            amortYears: sourceInputs.amortYears ?? 30,
            annualRent: sourceInputs.annualRent ?? sourceInputs.initialGrossRent ?? 0,
            vacancyRate: sourceInputs.vacancyRate ?? 0,
            annualTaxes: sourceInputs.annualTaxes ?? 0,
            annualInsurance: sourceInputs.annualInsurance ?? 0,
            annualMaintenance: sourceInputs.annualMaintenance ?? 0,
            annualHoa: sourceInputs.annualHoa ?? 0,
            propertyManagementRate: sourceInputs.propertyManagementRate ?? 0,
            reserveFundingMode: sourceInputs.reserveFundingMode ?? 'off',
            reservePercentOfRent: sourceInputs.reservePercentOfRent ?? 0,
            otherIncomeMonthly: sourceInputs.otherIncomeMonthly ?? 0,
            utilitiesMonthly: sourceInputs.utilitiesMonthly ?? 0,
            rentGrowth: sourceInputs.rentGrowth ?? 0,
            expenseGrowth: sourceInputs.expenseGrowth ?? 0,
            annualCapexReserve: sourceInputs.annualCapexReserve ?? 0,
            projectionMonths: sourceInputs.projectionMonths ?? 24,
            vacancyMode: sourceInputs.vacancyMode ?? 'smoothed',
            holdPeriod: sourceInputs.holdPeriod ?? 7,
            exitCapRate: sourceInputs.exitCapRate ?? 7.5,
            saleCosts: sourceInputs.saleCosts ?? 5,
            exitMethod: sourceInputs.exitMethod ?? 'cap',
            appreciationRate: sourceInputs.appreciationRate ?? 3,
            propertyReserves: hasExplicitReserveInputs ? (sourceInputs.propertyReserves ?? 0) : 0,
            portfolioReservesAllocated: hasExplicitReserveInputs ? (sourceInputs.portfolioReservesAllocated ?? 0) : 0,
            includeReserveReleaseOnSale: hasExplicitReserveInputs ? (sourceInputs.includeReserveReleaseOnSale ?? false) : false,
            landAllocationPercent: sourceInputs.landAllocationPercent ?? 20,
            buildingAllocationPercent: sourceInputs.buildingAllocationPercent ?? 80,
            capitalImprovements: sourceInputs.capitalImprovements ?? 0,
            placedInServiceDate: sourceInputs.placedInServiceDate ?? '',
            depreciationMethod: sourceInputs.depreciationMethod ?? 'residential_rental_sl_27_5',
            reserveInputsVersion: 1,
            investmentInputsVersion: 1
          };
          const historicalActuals: HistoricalActualYear[] = Array.isArray(property.historicalActuals)
            ? property.historicalActuals.map((entry: any) => ({
                id: entry.id || crypto.randomUUID(),
                year: Number(entry.year || DEFAULT_BASE_YEAR - 1),
                annualRent: Number(entry.annualRent || 0),
                annualTaxes: Number(entry.annualTaxes || 0),
                annualInsurance: Number(entry.annualInsurance || 0),
                annualHoa: Number(entry.annualHoa || 0),
                annualMaintenance: Number(entry.annualMaintenance || 0),
                utilities: Number(entry.utilities || 0),
                notes: entry.notes || ''
              }))
            : [];

          const riskProfile: PropertyRiskProfile = normalizeRiskProfile(
            property.riskProfile || migrateLegacyRiskProfile(property.currentYearInputs || property.inputs || createDefaultRiskProfile())
          );
          const migratedScenarioEvents: PropertyModelEvent[] = property.modelEvents || [];
          const legacyScenarioEvents: PropertyModelEvent[] = migratedScenarioEvents.length > 0 ? migratedScenarioEvents : [
            ...(riskProfile.scenario.turnoverCost.value > 0 ? [{
              id: crypto.randomUUID(),
              propertyId: id,
              name: 'Turnover Cost',
              type: 'one_time' as const,
              category: 'turnover' as const,
              valueType: 'amount' as const,
              amount: Number(riskProfile.scenario.turnoverCost.value || 0),
              year: Number(riskProfile.scenario.turnoverCost.appliesInYear || 1),
              startYear: null,
              endYear: null,
              sortOrder: 0,
              createdAt: Date.now(),
              notes: riskProfile.scenario.turnoverCost.notes || '',
              status: 'active' as const
            }] : []),
            ...(riskProfile.scenario.makeReadyCost.value > 0 ? [{
              id: crypto.randomUUID(),
              propertyId: id,
              name: 'Make-Ready Cost',
              type: 'one_time' as const,
              category: 'makeReady' as const,
              valueType: 'amount' as const,
              amount: Number(riskProfile.scenario.makeReadyCost.value || 0),
              year: Number(riskProfile.scenario.makeReadyCost.appliesInYear || 1),
              startYear: null,
              endYear: null,
              sortOrder: 0,
              createdAt: Date.now(),
              notes: riskProfile.scenario.makeReadyCost.notes || '',
              status: 'active' as const
            }] : []),
            ...(riskProfile.scenario.handymanCost.value > 0 ? [{
              id: crypto.randomUUID(),
              propertyId: id,
              name: 'Handyman Budget',
              type: 'one_time' as const,
              category: 'maintenance' as const,
              valueType: 'amount' as const,
              amount: Number(riskProfile.scenario.handymanCost.value || 0),
              year: Number(riskProfile.scenario.handymanCost.appliesInYear || 1),
              startYear: null,
              endYear: null,
              sortOrder: 0,
              createdAt: Date.now(),
              notes: riskProfile.scenario.handymanCost.notes || '',
              status: 'active' as const
            }] : []),
            ...(riskProfile.eventSchedule || []).map((event: any) => ({
              id: event.id || crypto.randomUUID(),
              propertyId: id,
              name: event.label || 'Modeled Event',
              type: 'one_time' as const,
              category: event.category || 'expense',
              valueType: 'amount' as const,
              amount: Number(event.amount || 0),
              year: Math.max(1, Math.ceil(Number(event.month || 1) / 12)),
              startYear: null,
              endYear: null,
              sortOrder: 0,
              createdAt: Date.now(),
              notes: event.notes || '',
              status: 'active' as const
            }))
          ];
          const normalizedModelEvents: PropertyModelEvent[] = legacyScenarioEvents.map((event: PropertyModelEvent, index: number) => ({
            ...event,
            sortOrder: Number(event.sortOrder ?? index)
          }));
          const legacyScenarioItems = [
            ...migrateLegacyScenarioItems(normalizedModelEvents),
            ...(riskProfile.minorItems || []).map((item: any) => ({
              id: item.id || crypto.randomUUID(),
              label: item.label || 'Minor repair',
              type: 'repair' as const,
              valueType: 'fixed' as const,
              value: Number(item.estimatedCost || 0),
              year: Math.max(1, Math.ceil(Number(item.timingMonths || 12) / 12)),
              startYear: null,
              endYear: null,
              recurrence: 'one_time' as const,
              notes: item.notes || ''
            })),
            ...(riskProfile.deferredMaintenance || []).map((item: any) => ({
              id: item.id || crypto.randomUUID(),
              label: item.title || 'Deferred maintenance',
              type: 'repair' as const,
              valueType: 'fixed' as const,
              value: Number(item.estimatedCost || 0),
              year: Math.max(1, Math.ceil(Number(item.timingMonths || 12) / 12)),
              startYear: null,
              endYear: null,
              recurrence: 'one_time' as const,
              notes: item.notes || ''
            }))
          ].filter((item) => Number(item.value || 0) > 0);
          const cleanedRiskProfile: PropertyRiskProfile = {
            ...riskProfile,
            minorItems: [],
            deferredMaintenance: [],
            scenario: {
              vacancyMonths: { value: 0, appliesInYear: 1, notes: '' },
              turnoverCost: { value: 0, appliesInYear: 1, notes: '' },
              handymanCost: { value: 0, appliesInYear: 1, notes: '' },
              makeReadyCost: { value: 0, appliesInYear: 1, notes: '' }
            },
            eventSchedule: []
          };
          const savedScenarios: SavedModelScenario[] = Array.isArray(property.savedScenarios)
            ? stripDerivedScenarioFields(property.savedScenarios.map((scenario: any) => ({
                id: scenario.id || crypto.randomUUID(),
                propertyId: id,
                name: scenario.name || 'Saved Scenario',
                items: Array.isArray(scenario.items)
                  ? scenario.items.map((item: any) => ({
                      id: item.id || crypto.randomUUID(),
                      label: item.label || '',
                      type: item.type || 'expense',
                      valueType: item.valueType || 'fixed',
                      value: Number(item.value || 0),
                      year: Number(item.year || item.startYear || 1),
                      startYear: item.startYear == null ? null : Number(item.startYear),
                      endYear: item.endYear == null ? null : Number(item.endYear),
                      recurrence: item.recurrence || 'one_time',
                      notes: item.notes || ''
                    }))
                  : migrateLegacyScenarioItems(Array.isArray(scenario.inputs?.events) ? scenario.inputs.events : []),
                summary: null,
                createdAt: Number(scenario.createdAt || Date.now()),
                lastRunAt: null
              })))
            : [];
          const importedScenarioNeeded = legacyScenarioItems.length > 0 && !savedScenarios.some((scenario) => scenario.name === 'Imported Legacy Adjustments');
          const normalizedSavedScenarios = importedScenarioNeeded
            ? [{
                id: crypto.randomUUID(),
                propertyId: id,
                name: 'Imported Legacy Adjustments',
                items: legacyScenarioItems.map((item) => ({ ...item, id: crypto.randomUUID() })),
                summary: null,
                createdAt: Date.now(),
                lastRunAt: null
              }, ...savedScenarios]
            : savedScenarios;
          const auditLog: PropertyAuditRecord[] = Array.isArray(property.auditLog)
            ? property.auditLog.map((entry: any) => ({
                id: entry.id || crypto.randomUUID(),
                propertyId: canonicalId,
                kind: entry.kind || 'baseline_edit',
                title: entry.title || 'Change recorded',
                detail: entry.detail || '',
                createdAt: Number(entry.createdAt || Date.now())
              }))
            : [];

          return [canonicalId, {
            ...property,
            id: canonicalId,
            supabaseId: property.supabaseId ?? null,
            ownershipEntity: property.ownershipEntity ?? '',
            address: property.address || '',
            baseYear: Number(property.baseYear || DEFAULT_BASE_YEAR),
            currentYearInputs: baseInputs,
            historicalActuals,
            inputs: baseInputs,
            riskProfile: cleanedRiskProfile,
            modelEvents: [],
            savedScenarios: normalizedSavedScenarios,
            auditLog
          }];
        });
      const migrated = Object.fromEntries(migratedEntries);
      setProperties(migrated);
      if (savedOrder) {
        const normalizedOrder = (JSON.parse(savedOrder) as string[])
          .map((id: string) => propertyIdMap.get(id) || id)
          .filter((id: string, index: number, list: string[]) => list.indexOf(id) === index && Boolean(migrated[id]));
        setPropertyOrder(normalizedOrder);
      } else {
        setPropertyOrder(Object.keys(migrated));
      }
    }
    if (savedPortfolios) {
      const parsedPortfolios = JSON.parse(savedPortfolios);
      setPortfolios(Object.fromEntries(
        Object.entries(parsedPortfolios).map(([id, portfolio]: [string, any]) => [id, {
          ...portfolio,
          propertyIds: Array.isArray(portfolio.propertyIds)
            ? portfolio.propertyIds.map((propertyId: string) => propertyIdMap.get(propertyId) || propertyId)
            : []
        }])
      ));
    }
    const debtIdMap = new Map<string, string>();

    if (savedDebts) {
      const parsedDebts = JSON.parse(savedDebts);
      const normalizedDebtEntries = Object.entries(parsedDebts).map(([id, debt]: [string, any]) => {
        const normalizedDebt = normalizeDebtRecord({
          ...debt,
          id,
          ownershipEntity: debt.ownershipEntity ?? '',
          isSharedPortfolioDebt: Boolean(debt.isSharedPortfolioDebt),
          balloonAmount: Number(debt.balloonAmount || 0),
          creditLimit: Number(debt.creditLimit || 0),
          createdAt: Number(debt.createdAt || Date.now()),
          updatedAt: Number(debt.updatedAt || Date.now())
        });
        const canonicalId = normalizedDebt.supabaseId || normalizedDebt.id;
        debtIdMap.set(id, canonicalId);
        debtIdMap.set(normalizedDebt.id, canonicalId);
        if (normalizedDebt.supabaseId) {
          debtIdMap.set(normalizedDebt.supabaseId, canonicalId);
        }
        return normalizedDebt;
      });
      setDebts(buildCanonicalDebtMap(normalizedDebtEntries));
    }
    if (savedDebtLinks) {
      const parsedLinks = JSON.parse(savedDebtLinks) as PropertyDebtLink[];
      setDebtLinks(parsedLinks.map((link) => ({
        ...link,
        debtId: debtIdMap.get(link.debtId) || link.debtId,
        propertyId: propertyIdMap.get(link.propertyId) || link.propertyId,
        valueBasis: link.valueBasis || 'purchase_price',
        manualValueOverride: link.manualValueOverride ?? null
      })));
    }
    if (savedDebtEvents) {
      const parsedEvents = JSON.parse(savedDebtEvents) as any[];
      setDebtEvents(parsedEvents.map((event: any) => ({
        id: event.id || crypto.randomUUID(),
        debtId: debtIdMap.get(event.debtId) || event.debtId,
        date: event.date || new Date().toISOString().slice(0, 10),
        type: event.type || 'extra_principal_payment',
        amount: Number(event.amount || 0),
        notes: event.notes || '',
        createdAt: Number(event.createdAt || Date.now()),
        updatedAt: Number(event.updatedAt || Date.now())
      })));
    }
    
    setIsLoaded(true);
  }, [buildCanonicalDebtMap]);

  // Save to LocalStorage
  useEffect(() => {
    if (isLoaded) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(buildPropertiesCachePayload(properties)));
      localStorage.setItem(ORDER_KEY, JSON.stringify(propertyOrder));
      localStorage.setItem(PORTFOLIO_KEY, JSON.stringify(portfolios));
      localStorage.setItem(DEBT_KEY, JSON.stringify(debts));
      localStorage.setItem(DEBT_LINK_KEY, JSON.stringify(debtLinks));
      localStorage.setItem(DEBT_EVENT_KEY, JSON.stringify(debtEvents));
    }
  }, [properties, propertyOrder, portfolios, debts, debtLinks, debtEvents, isLoaded]);

  useEffect(() => {
    if (!isLoaded) return;
    setDebts((prev: Record<string, DebtRecord>) => (
      Object.fromEntries(
        Object.entries(prev).map(([id, debt]) => [id, withCalculatedDebtPayment(debt, debtEvents.filter((event: DebtEvent) => event.debtId === id))])
      )
    ));
  }, [debtEvents, isLoaded]);

  const saveProperty = useCallback((
    id: string,
    name: string,
    address: string,
    ownershipEntity: string,
    baseYear: number,
    currentYearInputs: FinancialInputs,
    historicalActuals: HistoricalActualYear[],
    riskProfile: PropertyRiskProfile,
    modelEvents: PropertyModelEvent[],
    savedScenarios: SavedModelScenario[],
    auditLog: PropertyAuditRecord[],
    metrics: ModelMetrics,
    options?: {
      supabaseId?: string | null;
      teamId?: string | null;
    }
  ) => {
    const now = Date.now();
    const canonicalId = options?.supabaseId || id;
    setProperties((prev: any) => {
      const existing = prev[canonicalId] || prev[id];
      const nextSupabaseId = options?.supabaseId ?? existing?.supabaseId ?? null;
      const nextTeamId = options?.teamId ?? existing?.teamId ?? null;
      if (existing && existing.name === name &&
          existing.address === address &&
          (existing.ownershipEntity || '') === ownershipEntity &&
          (existing.supabaseId ?? null) === nextSupabaseId &&
          (existing.teamId ?? null) === nextTeamId &&
          existing.baseYear === baseYear &&
          JSON.stringify(existing.currentYearInputs || existing.inputs) === JSON.stringify(currentYearInputs) &&
          JSON.stringify(existing.historicalActuals || []) === JSON.stringify(historicalActuals) &&
          JSON.stringify(existing.riskProfile) === JSON.stringify(riskProfile) &&
          JSON.stringify(existing.modelEvents || []) === JSON.stringify(modelEvents) &&
          JSON.stringify(existing.savedScenarios || []) === JSON.stringify(savedScenarios) &&
          JSON.stringify(existing.auditLog || []) === JSON.stringify(auditLog)) {
        return prev;
      }
      const next = { ...prev };
      if (canonicalId !== id) {
        delete next[id];
      }
      return {
        ...next,
        [canonicalId]: {
          id: canonicalId,
          supabaseId: nextSupabaseId,
          teamId: nextTeamId,
          detailLevel: 'full',
          name,
          address,
          ownershipEntity,
          createdAt: existing?.createdAt || now,
          updatedAt: now,
          baseYear,
          currentYearInputs,
          historicalActuals,
          inputs: currentYearInputs,
          riskProfile,
          modelEvents,
          savedScenarios,
          auditLog,
          metrics
        }
      };
    });
    setPropertyOrder((prev: any) => {
      const nextOrder = prev.map((propertyId: string) => propertyId === id ? canonicalId : propertyId);
      return nextOrder.includes(canonicalId) ? nextOrder : [...nextOrder, canonicalId];
    });
  }, []);

  const deleteProperty = useCallback((id: string) => {
    setProperties((prev: any) => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
    setPropertyOrder((prev: any) => prev.filter((pid: any) => pid !== id));
    setPortfolios((prev: any) => {
      const next = { ...prev };
      Object.keys(next).forEach((key: any) => {
        next[key].propertyIds = next[key].propertyIds.filter((pid: any) => pid !== id);
      });
      return next;
    });
    setDebtLinks((prev: PropertyDebtLink[]) => prev.filter((link) => link.propertyId !== id));
  }, []);

  const createPortfolio = useCallback((name: string) => {
    const id = crypto.randomUUID();
    const now = Date.now();
    setPortfolios((prev: any) => ({
      ...prev,
      [id]: { id, name, propertyIds: [], createdAt: now, updatedAt: now }
    }));
    return id;
  }, []);

  const addPropertyToPortfolio = useCallback((targetPortfolioId: string, propertyId: string) => {
    if (shouldLogPortfolioDnD) {
      console.info('[Portfolio DnD] addPropertyToPortfolio called', {
        targetPortfolioId,
        propertyId,
        hasProperty: Boolean(properties[propertyId]),
        hasPortfolio: Boolean(portfolios[targetPortfolioId])
      });
    }
    if (!properties[propertyId]) return 'missing_property' as const;
    if (!portfolios[targetPortfolioId]) return 'missing_portfolio' as const;
    if (portfolios[targetPortfolioId].propertyIds.includes(propertyId)) return 'duplicate' as const;

    setPortfolios((prev: any) => {
      const next = { ...prev };
      const target = next[targetPortfolioId];
      if (target && !target.propertyIds.includes(propertyId)) {
        next[targetPortfolioId] = {
          ...target,
          propertyIds: [...target.propertyIds, propertyId],
          updatedAt: Date.now()
        };
      }
      return next;
    });
    if (shouldLogPortfolioDnD) {
      console.info('[Portfolio DnD] portfolio membership saved', {
        targetPortfolioId,
        propertyId
      });
    }
    return 'added' as const;
  }, [portfolios, properties]);

  const removePropertyFromPortfolio = useCallback((portfolioId: string, propertyId: string) => {
    setPortfolios((prev: any) => {
      const next = { ...prev };
      const target = next[portfolioId];
      if (target) {
        next[portfolioId] = {
          ...target,
          propertyIds: target.propertyIds.filter((id: any) => id !== propertyId),
          updatedAt: Date.now()
        };
      }
      return next;
    });
  }, []);

  const deletePortfolio = useCallback((id: string) => {
    setPortfolios((prev: any) => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
  }, []);

  const updatePortfolio = useCallback((id: string, updates: Partial<Portfolio>) => {
    setPortfolios((prev: any) => {
      if (!prev[id]) return prev;
      return {
        ...prev,
        [id]: { ...prev[id], ...updates, updatedAt: Date.now() }
      };
    });
  }, []);

  const replacePortfolios = useCallback((nextPortfolios: Record<string, Portfolio>) => {
    setPortfolios((prev: Record<string, Portfolio>) => (
      Object.fromEntries(
        Object.entries(nextPortfolios).map(([id, portfolio]) => [id, {
          ...portfolio,
          propertyIds: portfolio.propertyIds.length > 0 ? portfolio.propertyIds : (prev[id]?.propertyIds || [])
        }])
      )
    ));
  }, []);

  const replacePortfolioMemberships = useCallback((memberships: Array<{ portfolioId: string; propertyId: string }>) => {
    setPortfolios((prev: Record<string, Portfolio>) => (
      Object.fromEntries(
        Object.entries(prev).map(([id, portfolio]) => [id, {
          ...portfolio,
          propertyIds: memberships
            .filter((membership) => membership.portfolioId === id)
            .map((membership) => membership.propertyId),
          updatedAt: portfolio.updatedAt || Date.now()
        }])
      )
    ));
  }, []);

  const reorderProperties = useCallback((newOrder: string[]) => {
    setPropertyOrder(newOrder);
  }, []);

  const replaceProperties = useCallback((nextProperties: Record<string, Property>, nextOrder?: string[]) => {
    setProperties((prev: Record<string, Property>) => {
      const merged = { ...prev };

      Object.entries(nextProperties).forEach(([id, property]) => {
        const existing = prev[id];
        merged[id] = property.detailLevel === 'summary' && existing?.detailLevel === 'full'
          ? {
              ...existing,
              ...property,
              detailLevel: 'full'
            }
          : property;
      });

      Object.keys(merged).forEach((id) => {
        if (!nextProperties[id]) {
          delete merged[id];
        }
      });

      return merged;
    });
    if (nextOrder) {
      setPropertyOrder(nextOrder);
    }
  }, []);

  const saveDebt = useCallback((debt: DebtRecord) => {
    const now = Date.now();
    setDebts((prev: Record<string, DebtRecord>) => {
      const canonicalId = debt.supabaseId || debt.id;
      const existing = prev[canonicalId] || prev[debt.id] || Object.values(prev).find((entry) => entry.supabaseId && entry.supabaseId === canonicalId) || null;

      // Canonicalize synced debts under their Supabase id so the same loan
      // cannot survive in state once as a local UUID and again as a remote UUID.
      const next = Object.fromEntries(
        Object.entries(prev).filter(([id, entry]) => (
          id !== debt.id &&
          id !== canonicalId &&
          entry.supabaseId !== canonicalId
        ))
      ) as Record<string, DebtRecord>;

      next[canonicalId] = withCalculatedDebtPayment({
        ...debt,
        id: canonicalId,
        supabaseId: debt.supabaseId ?? existing?.supabaseId ?? null,
        propertyId: debt.propertyId ?? existing?.propertyId ?? null,
        teamId: debt.teamId ?? existing?.teamId ?? null,
        createdAt: debt.createdAt || existing?.createdAt || now,
        updatedAt: now
      }, debtEvents.filter((event: DebtEvent) => event.debtId === debt.id || event.debtId === canonicalId));

      return next;
    });
  }, [debtEvents]);

  const deleteDebt = useCallback((id: string) => {
    setDebts((prev: Record<string, DebtRecord>) => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
    setDebtLinks((prev: PropertyDebtLink[]) => prev.filter((link) => link.debtId !== id));
    setDebtEvents((prev: DebtEvent[]) => prev.filter((event) => event.debtId !== id));
  }, []);

  const saveDebtEvent = useCallback((event: DebtEvent) => {
    const now = Date.now();
    const normalizedEvent: DebtEvent = {
      ...event,
      amount: Math.max(0, Number(event.amount || 0)),
      notes: event.notes || '',
      createdAt: event.createdAt || now,
      updatedAt: now
    };
    setDebtEvents((prev: DebtEvent[]) => {
      const withoutExisting = prev.filter((existing) => existing.id !== normalizedEvent.id);
      const nextEvents = [...withoutExisting, normalizedEvent].sort((left, right) => compareEventOrder(left, right));

      setDebts((currentDebts: Record<string, DebtRecord>) => {
        const debt = currentDebts[normalizedEvent.debtId];
        if (!debt) return currentDebts;
        return {
          ...currentDebts,
          [normalizedEvent.debtId]: withCalculatedDebtPayment(
            debt,
            nextEvents.filter((existing) => existing.debtId === normalizedEvent.debtId)
          )
        };
      });

      return nextEvents;
    });
  }, []);

  const deleteDebtEvent = useCallback((eventId: string) => {
    setDebtEvents((prev: DebtEvent[]) => {
      const eventToDelete = prev.find((event) => event.id === eventId) || null;
      const nextEvents = prev.filter((event) => event.id !== eventId);
      if (eventToDelete) {
        setDebts((currentDebts: Record<string, DebtRecord>) => {
          const debt = currentDebts[eventToDelete.debtId];
          if (!debt) return currentDebts;
          return {
            ...currentDebts,
            [eventToDelete.debtId]: withCalculatedDebtPayment(debt, nextEvents.filter((event) => event.debtId === eventToDelete.debtId))
          };
        });
      }
      return nextEvents;
    });
  }, []);

  const replaceDebtEvents = useCallback((nextEvents: DebtEvent[]) => {
    const normalizedEvents = [...nextEvents]
      .map((event) => ({
        ...event,
        amount: Math.max(0, Number(event.amount || 0)),
        notes: event.notes || '',
        createdAt: Number(event.createdAt || Date.now()),
        updatedAt: Number(event.updatedAt || Date.now())
      }))
      .sort((left, right) => compareEventOrder(left, right));

    setDebtEvents(normalizedEvents);
    setDebts((prev: Record<string, DebtRecord>) => (
      Object.fromEntries(
        Object.entries(prev).map(([id, debt]) => [id, withCalculatedDebtPayment(debt, normalizedEvents.filter((event) => event.debtId === id))])
      )
    ));
  }, []);

  const upsertDebtLink = useCallback((link: PropertyDebtLink) => {
    setDebtLinks((prev: PropertyDebtLink[]) => {
      const withoutDuplicate = prev.filter((existing) => !(existing.debtId === link.debtId && existing.propertyId === link.propertyId) && existing.id !== link.id);
      return [...withoutDuplicate, {
        ...link,
        teamId: link.teamId ?? prev.find((existing) => existing.id === link.id)?.teamId ?? null
      }];
    });
  }, []);

  const removeDebtLink = useCallback((linkId: string) => {
    setDebtLinks((prev: PropertyDebtLink[]) => prev.filter((link) => link.id !== linkId));
  }, []);

  const replaceDebtLinks = useCallback((nextLinks: PropertyDebtLink[]) => {
    setDebtLinks(nextLinks);
  }, []);

  const replaceDebts = useCallback((nextDebts: DebtRecord[]) => {
    setDebts(buildCanonicalDebtMap(nextDebts));
  }, [buildCanonicalDebtMap]);

  const autoAllocateDebtByAcquisitionPrice = useCallback((debtId: string) => {
    setDebtLinks((prev: PropertyDebtLink[]) => {
      const relevant = prev.filter((link) => link.debtId === debtId);
      const totalPurchasePrice = relevant.reduce((sum, link) => sum + (properties[link.propertyId]?.inputs.purchasePrice || 0), 0);
      return prev.map((link) => {
        if (link.debtId !== debtId) return link;
        const propertyPurchasePrice = properties[link.propertyId]?.inputs.purchasePrice || 0;
        return {
          ...link,
          allocationMethod: 'acquisition_price',
          allocationPercent: totalPurchasePrice > 0 ? (propertyPurchasePrice / totalPurchasePrice) * 100 : 0
        };
      });
    });
  }, [properties]);

  const replacePropertyDebts = useCallback((propertyId: string, nextDebts: DebtRecord[]) => {
    const nextDebtIds = nextDebts.map((debt) => debt.id);
    setDebts((prev: Record<string, DebtRecord>) => {
      const previousDebtIdsForProperty = Object.values(prev)
        .filter((debt) => debt.propertyId === propertyId)
        .map((debt) => debt.id);
      const removedDebtIds = previousDebtIdsForProperty.filter((debtId) => !nextDebtIds.includes(debtId));

      if (removedDebtIds.length > 0) {
        setDebtEvents((currentEvents: DebtEvent[]) => currentEvents.filter((event) => !removedDebtIds.includes(event.debtId)));
      }

      const preserved = Object.fromEntries(
        Object.entries(prev).filter(([, debt]) => debt.propertyId !== propertyId)
      ) as Record<string, DebtRecord>;
      nextDebts.forEach((debt) => {
        preserved[debt.id] = debt;
      });
      return preserved;
    });
    setDebtLinks((prev: PropertyDebtLink[]) => {
      const preserved = prev.filter((link) => link.propertyId !== propertyId);
      const generated = nextDebts.map((debt) => ({
        id: `property-debt-link:${propertyId}:${debt.id}`,
        debtId: debt.id,
        propertyId,
        teamId: debt.teamId ?? null,
        allocationPercent: 100,
        allocationMethod: 'manual' as const
      }));
      return [...preserved, ...generated];
    });
  }, []);

  const resetWorkspaceData = useCallback((options?: { clearPersistedCache?: boolean }) => {
    setProperties({});
    setPortfolios({});
    setPropertyOrder([]);
    setDebts({});
    setDebtLinks([]);
    setDebtEvents([]);
    if (options?.clearPersistedCache) {
      localStorage.removeItem(STORAGE_KEY);
      localStorage.removeItem(PORTFOLIO_KEY);
      localStorage.removeItem(ORDER_KEY);
      localStorage.removeItem(DEBT_KEY);
      localStorage.removeItem(DEBT_LINK_KEY);
      localStorage.removeItem(DEBT_EVENT_KEY);
    }
  }, []);

  return {
    properties,
    portfolios,
    propertyOrder,
    debts,
    debtLinks,
    debtEvents,
    saveProperty,
    deleteProperty,
    createPortfolio,
    addPropertyToPortfolio,
    removePropertyFromPortfolio,
    deletePortfolio,
    updatePortfolio,
    replaceProperties,
    replacePortfolios,
    replacePortfolioMemberships,
    reorderProperties,
    saveDebt,
    deleteDebt,
    replacePropertyDebts,
    replaceDebts,
    saveDebtEvent,
    deleteDebtEvent,
    replaceDebtEvents,
    upsertDebtLink,
    replaceDebtLinks,
    removeDebtLink,
    autoAllocateDebtByAcquisitionPrice,
    resetWorkspaceData,
    isLoaded
  };
};
